import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo";
import {
  ArrowLeft,
  Megaphone,
  Play,
  Pause,
  Sparkles,
  Globe2,
  CheckCircle2,
} from "lucide-react";

const CITIES = [
  { id: "nyc", name: "New York", cpm: 42, daily: 18000, img: "https://images.unsplash.com/photo-1543853016-2ce0a3a55e7c?auto=format&fit=crop&w=1600&q=80" },
  { id: "lon", name: "London", cpm: 38, daily: 14500, img: "https://images.unsplash.com/photo-1505761671935-60b3a7427bad?auto=format&fit=crop&w=1600&q=80" },
  { id: "tok", name: "Tokyo", cpm: 35, daily: 16200, img: "https://images.unsplash.com/photo-1542051841857-5f90071e7989?auto=format&fit=crop&w=1600&q=80" },
  { id: "sin", name: "Singapore", cpm: 32, daily: 9800, img: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?auto=format&fit=crop&w=1600&q=80" },
  { id: "dxb", name: "Dubai", cpm: 30, daily: 11200, img: "https://images.unsplash.com/photo-1518684079-3c830dcef090?auto=format&fit=crop&w=1600&q=80" },
  { id: "blr", name: "Bengaluru", cpm: 12, daily: 22000, img: "https://images.unsplash.com/photo-1596176530529-78163a4f7af2?auto=format&fit=crop&w=1600&q=80" },
];

const CREATIVES = [
  { name: "Brand Anthem", url: "https://images.unsplash.com/photo-1558655146-d09347e92766?auto=format&fit=crop&w=1600&q=80" },
  { name: "Product Launch", url: "https://images.unsplash.com/photo-1611930022073-b7a4ba5fcccd?auto=format&fit=crop&w=1600&q=80" },
  { name: "Holiday Sale", url: "https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?auto=format&fit=crop&w=1600&q=80" },
];

export default function DemoAdvertise() {
  const [step, setStep] = useState(1); // 1=pick cities, 2=pick creative, 3=launched
  const [selectedCities, setSelectedCities] = useState(["nyc", "tok", "blr"]);
  const [budget, setBudget] = useState(5000);
  const [creativeIdx, setCreativeIdx] = useState(0);
  const [running, setRunning] = useState(true);
  const [impressions, setImpressions] = useState(0);
  const [spent, setSpent] = useState(0);
  const [activeCity, setActiveCity] = useState(0);
  const tickRef = useRef(null);

  const cities = useMemo(
    () => CITIES.filter((c) => selectedCities.includes(c.id)),
    [selectedCities]
  );

  const totalDaily = cities.reduce((a, c) => a + c.daily, 0);
  const avgCpm = cities.length
    ? cities.reduce((a, c) => a + c.cpm, 0) / cities.length
    : 0;
  const monthlyAudience = totalDaily * 30;

  // Live counter when running
  useEffect(() => {
    if (step !== 3 || !running) {
      clearInterval(tickRef.current);
      return;
    }
    tickRef.current = setInterval(() => {
      const inc = Math.max(8, Math.round(totalDaily / 86400 * 5)); // ~5s of real-world traffic per tick
      setImpressions((i) => i + inc);
      setSpent((s) => Math.min(budget, +(s + (inc / 1000) * avgCpm).toFixed(2)));
      setActiveCity((a) => (cities.length ? (a + 1) % cities.length : 0));
    }, 800);
    return () => clearInterval(tickRef.current);
  }, [step, running, totalDaily, avgCpm, budget, cities.length]);

  const toggleCity = (id) => {
    setSelectedCities((s) =>
      s.includes(id) ? s.filter((x) => x !== id) : [...s, id]
    );
  };

  const reset = () => {
    setStep(1);
    setImpressions(0);
    setSpent(0);
    setActiveCity(0);
    setRunning(true);
  };

  const currentCity = cities[activeCity] || cities[0];
  const creative = CREATIVES[creativeIdx];

  return (
    <div className="bg-[#0A0908] text-white min-h-screen font-sans">
      <header className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <BrandLogo size={30} />
            <span className="font-display font-extrabold tracking-tight text-[16px]">Mirrorloops</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/demo" className="text-sm text-white/60 hover:text-white flex items-center gap-2">
              <ArrowLeft size={14} /> All demos
            </Link>
            <Link to="/register" className="text-xs px-3 py-1.5 bg-white text-black font-semibold">
              Get started
            </Link>
          </div>
        </div>
      </header>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-12 md:py-16">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#00E5FF] mb-3 flex items-center gap-2">
          <Megaphone size={12} /> Demo · DOOH advertising
        </div>
        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-4xl md:text-6xl">
          Launch a demo campaign.
        </h1>
        <p className="mt-4 text-white/60 max-w-xl">
          Pick markets, choose a creative, set a budget. Then watch live
          impressions roll in on a simulated Mirrorloops screen.
        </p>

        <div className="mt-10 grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
          {/* LEFT — Builder / Live KPIs */}
          <div className="space-y-6">
            {/* Step header */}
            <div className="flex items-center gap-2 text-xs">
              {[1, 2, 3].map((n) => (
                <div
                  key={n}
                  className={`flex items-center gap-2 px-3 py-1 border ${
                    step >= n
                      ? "border-[#00E5FF] text-[#00E5FF]"
                      : "border-white/10 text-white/40"
                  }`}
                  data-testid={`demo-ad-step-${n}`}
                >
                  <span className="font-mono">0{n}</span>
                  <span>{n === 1 ? "Cities" : n === 2 ? "Creative" : "Live"}</span>
                </div>
              ))}
            </div>

            {step === 1 && (
              <div className="space-y-6" data-testid="demo-ad-step-cities">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3">
                    Pick markets
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {CITIES.map((c) => {
                      const on = selectedCities.includes(c.id);
                      return (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => toggleCity(c.id)}
                          data-testid={`demo-ad-city-${c.id}`}
                          className={`text-left p-4 border transition ${
                            on
                              ? "border-[#00E5FF] bg-[#00E5FF]/5"
                              : "border-white/10 bg-white/[0.02] hover:border-white/30"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-display font-extrabold text-lg">{c.name}</span>
                            {on && <CheckCircle2 size={16} className="text-[#00E5FF]" />}
                          </div>
                          <div className="font-mono text-[11px] text-white/50 mt-2">
                            CPM ${c.cpm} · {c.daily.toLocaleString()} / day
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-2">
                    Daily budget
                  </div>
                  <input
                    type="range"
                    min={500}
                    max={50000}
                    step={500}
                    value={budget}
                    onChange={(e) => setBudget(+e.target.value)}
                    data-testid="demo-ad-budget"
                    className="w-full accent-[#00E5FF]"
                  />
                  <div className="flex items-center justify-between mt-2 font-mono text-sm">
                    <span className="text-white/40">$500</span>
                    <span className="text-[#00E5FF] font-bold text-lg" data-testid="demo-ad-budget-value">
                      ${budget.toLocaleString()} / day
                    </span>
                    <span className="text-white/40">$50,000</span>
                  </div>
                </div>

                {/* Pricing preview */}
                <div className="border border-white/10 bg-white/[0.02] p-5">
                  <div className="grid grid-cols-3 gap-4">
                    <KPI label="Avg CPM" value={`$${avgCpm.toFixed(0)}`} accent="#00E5FF" />
                    <KPI label="Daily impr." value={totalDaily.toLocaleString()} />
                    <KPI label="Monthly audience" value={monthlyAudience.toLocaleString()} accent="#00E5FF" />
                  </div>
                </div>

                <button
                  onClick={() => setStep(2)}
                  disabled={!cities.length}
                  data-testid="demo-ad-next-1"
                  className="w-full py-4 bg-[#00E5FF] text-black font-bold disabled:opacity-30"
                >
                  Pick creative →
                </button>
              </div>
            )}

            {step === 2 && (
              <div className="space-y-6" data-testid="demo-ad-step-creative">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3">
                    Pick creative
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {CREATIVES.map((c, i) => (
                      <button
                        key={c.name}
                        type="button"
                        onClick={() => setCreativeIdx(i)}
                        data-testid={`demo-ad-creative-${i}`}
                        className={`overflow-hidden border transition ${
                          creativeIdx === i
                            ? "border-[#00E5FF] ring-1 ring-[#00E5FF]"
                            : "border-white/10"
                        }`}
                      >
                        <div className="aspect-video bg-black">
                          <img src={c.url} alt={c.name} className="w-full h-full object-cover" />
                        </div>
                        <div className="p-2 text-xs font-semibold text-left">{c.name}</div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setStep(1)}
                    className="px-5 py-3 border border-white/15 text-white/70 hover:border-white"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => { setStep(3); setImpressions(0); setSpent(0); setRunning(true); }}
                    data-testid="demo-ad-launch"
                    className="flex-1 py-3 bg-[#00E5FF] text-black font-bold inline-flex items-center justify-center gap-2"
                  >
                    <Sparkles size={14} /> Launch demo campaign
                  </button>
                </div>
              </div>
            )}

            {step === 3 && (
              <div className="space-y-6" data-testid="demo-ad-step-live">
                <div className="border border-[#00E5FF]/30 bg-gradient-to-br from-[#001a1f] to-[#0A0908] p-6">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#00E5FF]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse" />
                      Live
                    </div>
                    <button
                      onClick={() => setRunning((r) => !r)}
                      data-testid="demo-ad-toggle-run"
                      className="text-xs px-3 py-1 border border-white/10 hover:border-white/30 inline-flex items-center gap-1"
                    >
                      {running ? <><Pause size={12} /> Pause</> : <><Play size={12} /> Resume</>}
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <KPI label="Impressions" value={impressions.toLocaleString()} big accent="#00E5FF" testid="demo-ad-impressions" />
                    <KPI label="Spent" value={`$${spent.toFixed(2)}`} big testid="demo-ad-spent" />
                    <KPI label="Cities" value={cities.length} accent="#00E5FF" />
                  </div>
                </div>
                <div className="border border-white/10 bg-white/[0.02] p-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3">
                    Live activity
                  </div>
                  <div className="space-y-1 text-xs font-mono">
                    {cities.map((c, i) => (
                      <div
                        key={c.id}
                        className={`flex items-center justify-between py-1 ${
                          i === activeCity ? "text-[#00E5FF]" : "text-white/50"
                        }`}
                        data-testid={`demo-ad-live-${c.id}`}
                      >
                        <span>{c.name}</span>
                        <span>
                          {i === activeCity ? "● now playing" : `idle · CPM $${c.cpm}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={reset}
                    data-testid="demo-ad-reset"
                    className="flex-1 py-3 border border-white/15 text-white/80 hover:border-white"
                  >
                    Run again
                  </button>
                  <Link
                    to="/register"
                    data-testid="demo-ad-cta-real"
                    className="flex-1 py-3 bg-white text-black font-bold text-center"
                  >
                    Run a real campaign
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* RIGHT — DOOH simulator */}
          <div className="lg:sticky lg:top-6">
            <div className="aspect-[4/5] overflow-hidden border border-white/10 relative bg-black">
              <img
                src={currentCity?.img}
                alt=""
                className="w-full h-full object-cover opacity-90"
                key={currentCity?.id}
              />
              {/* Sim DOOH placard */}
              <div className="absolute inset-x-6 bottom-6 top-1/2 bg-black/80 backdrop-blur border border-[#00E5FF]/40 overflow-hidden flex items-center justify-center">
                {step === 3 ? (
                  <img src={creative.url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center p-6">
                    <Globe2 size={32} className="text-[#00E5FF] mx-auto mb-3" />
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">
                      Mirrorloops Demo Screen
                    </div>
                    <div className="font-display font-extrabold text-2xl mt-2">
                      Your ad here.
                    </div>
                  </div>
                )}
              </div>
              {/* Status pill */}
              <div className="absolute top-4 left-4 px-2 py-1 bg-black/60 border border-white/20 text-[10px] uppercase tracking-[0.22em] text-white/80">
                {currentCity?.name}
              </div>
              {step === 3 && (
                <div className="absolute top-4 right-4 px-2 py-1 bg-[#00E5FF] text-black text-[10px] font-bold uppercase tracking-[0.22em]">
                  Now playing
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-[11px] text-white/30">
        © Mirrorloops Opc Pvt. Ltd.  ·  Simulated demo · no real ad spend
      </footer>
    </div>
  );
}

function KPI({ label, value, big, accent, testid }) {
  return (
    <div data-testid={testid}>
      <div
        className={`font-mono font-bold ${big ? "text-3xl" : "text-2xl"}`}
        style={accent ? { color: accent } : {}}
      >
        {value}
      </div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mt-1">
        {label}
      </div>
    </div>
  );
}
